import {
  Affix,
  Flex,
  Form,
  Input,
  Select,
  Space,
  Typography,
  Badge,
  Spin,
  theme,
  Grid,
} from "antd";
import { useTranslate } from "#src/hooks";
import { getVideoTypeColor, getVideoTypeLabel } from "../helpers";
import { MediaStatus } from "../../components/MediaStatusComponent";
import { GlobalOutlined, LoadingOutlined } from "@ant-design/icons";
import { MetadataAsset, SortField, Video } from "../types";
import { useDebounce } from "use-debounce";
import { DEFAULT_SORT_FIELD } from "../constants";

export type BatchListFiltersProps = {
  allVideos: Video[];
  videos: Video[];
  isFetching: boolean;
  isMatching: boolean;
  setSearch: React.Dispatch<React.SetStateAction<string>>;
  setFilterValue: React.Dispatch<React.SetStateAction<string[]>>;
  setSortField: React.Dispatch<React.SetStateAction<SortField>>;
  showStatus: boolean;
};

export const BatchListFilters = ({
  allVideos,
  videos,
  isFetching,
  isMatching,
  setSearch,
  setFilterValue,
  setSortField,
  showStatus,
}: BatchListFiltersProps) => {
  const t = useTranslate();
  const { token } = theme.useToken();
  const breakpoint = Grid.useBreakpoint();
  const isMobile = breakpoint.lg === undefined ? false : !breakpoint.lg;

  const videoTypeOptions = [...new Set(allVideos.map((x) => x.metadata.type))]
    .map((value: string | undefined) => ({
      label: getVideoTypeLabel(value),
      value,
    }))
    .sort((a, b) => (a.label ?? "").localeCompare(b.label ?? ""));

  const marketOptions = [
    ...new Set(
      allVideos.map(
        (x) =>
          (x.metadata.assets as MetadataAsset[] | undefined)?.[0]
            ?.market as string
      )
    ),
  ]
    .map((value: string) => ({ label: getVideoTypeLabel(value), value }))
    .sort((a, b) => (a.label ?? "").localeCompare(b.label ?? ""));

  const statusOptions = [
    ...new Set(allVideos.map((video) => video.status)),
  ].map((status) => ({
    label: status,
    value: status,
  }));

  const searchVideo = (query: string) => {
    setSearch(query);
  };

  const [handleSearch] = useDebounce(searchVideo, 500);

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    handleSearch(e.currentTarget.value);
  };

  const handleFilterFieldChange = (value: string[]) => {
    setFilterValue(value);
  };

  const handleSortFieldChange = (value: SortField) => {
    setSortField(value);
  };

  return (
    <Affix offsetTop={0}>
      <Flex
        gap={10}
        vertical={isMobile}
        align={!isMobile ? "center" : "start"}
        justify="space-between"
        style={{
          marginBottom: 40,
          padding: "10px 0",
          background: token.colorBgContainer,
          boxShadow: `0 4px 6px -4px ${token.colorBorderSecondary}`,
        }}
      >
        <div>
          <Space size={"small"}>
            <Typography.Text style={{ opacity: 0.6 }}>
              {allVideos.length} {t("media.batches.list.loaded")}
              {videos.length} {t("media.batches.list.matches")}
            </Typography.Text>
            <Spin
              size="small"
              style={{ opacity: isFetching || isMatching ? 1 : 0 }}
              indicator={
                isFetching ? undefined : (
                  <LoadingOutlined style={{ fontSize: 20 }} spin />
                )
              }
            />
          </Space>
        </div>
        <Input.Search
          size="large"
          placeholder={t("media.batches.list.tickerOrName")}
          onSearch={handleSearch}
          onChange={handleSearchChange}
          style={{
            width: 300,
            boxShadow: "0 0 20px #00000020",
            borderRadius: 10,
          }}
        />
        <Flex vertical align={isMobile ? "start" : "end"} gap={5}>
          <Form.Item
            label={t("media.batches.list.sortBy")}
            style={{ margin: 0 }}
          >
            <Select
              size="middle"
              defaultValue={DEFAULT_SORT_FIELD}
              style={{ width: 250 }}
              onChange={handleSortFieldChange}
              options={[
                {
                  value: "name",
                  label: t("media.batches.list.stockName"),
                },
                {
                  value: "ticker",
                  label: t("media.batches.list.stockTicker"),
                },
                { value: "market", label: t("media.batches.list.market") },
                { value: "type", label: t("media.batches.list.template") },
                ...(showStatus
                  ? [
                      {
                        value: "status",
                        label: t("media.batches.list.status"),
                      },
                    ]
                  : []),
              ]}
            />
          </Form.Item>
          <Form.Item
            label={t("media.batches.list.filter")}
            style={{ margin: 0 }}
          >
            <Select
              mode="multiple"
              size="middle"
              style={{ width: 250 }}
              listHeight={400}
              onChange={handleFilterFieldChange}
            >
              <Select.OptGroup
                label={t("media.batches.list.type")}
                key={"type"}
              >
                {videoTypeOptions.map((option) => (
                  <Select.Option
                    key={option.value}
                    value={`type:${option.value}`}
                    label={option.label}
                  >
                    <Badge
                      color={getVideoTypeColor(option.value)}
                      text={option.label}
                    />
                  </Select.Option>
                ))}
              </Select.OptGroup>
              <Select.OptGroup
                label={t("media.batches.list.market")}
                key={"market"}
              >
                {marketOptions.map((option) => (
                  <Select.Option
                    key={option.value}
                    value={`market:${option.value}`}
                    label={option.label}
                  >
                    <GlobalOutlined /> {option.label}
                  </Select.Option>
                ))}
              </Select.OptGroup>
              {showStatus && (
                <Select.OptGroup
                  label={t("media.batches.list.status")}
                  key={"status"}
                >
                  {statusOptions.map((option) => (
                    <Select.Option
                      key={option.value}
                      value={`status:${option.value}`}
                    >
                      <MediaStatus
                        status={option.value ?? ""}
                        label={option.value}
                      />
                    </Select.Option>
                  ))}
                </Select.OptGroup>
              )}
            </Select>
          </Form.Item>
        </Flex>
      </Flex>
    </Affix>
  );
};
